import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import saveAs from 'file-saver';
import { TooltipModule } from 'primeng/tooltip';
import { ODDetailResponse } from 'src/app/models/FilesRequest';

@Component({
    selector: 'app-detail-documents',
    standalone: true,
    imports: [TranslateModule, TooltipModule, NgTemplateOutlet],
    templateUrl: './detail-documents.component.html'
})
export class DetailDocumentsComponent {
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);

    readonly headerLabel = input<string | undefined>();
    readonly contentLabel = input<string>('REQUEST_DETAIL.PROVIDED_DOCUMENTATION');
    readonly detail = input.required<ODDetailResponse>();
    readonly toRemove = input<string[]>([]);
    private readonly lang = toSignal(this.translate.onLangChange);

    protected areDocumentsDownloaded = computed(() => {
        const detail = this.detail();
        const isSuccessStatus = detail.personQuery.status === 'Success';
        const isDocumentsDowloadCompleted = detail.personQuery.isDocumentsDowloadCompleted;
        return isSuccessStatus && isDocumentsDowloadCompleted;
    });

    protected headerLabelTranslated = computed(() => this.headerLabel() ? this.translate.instant(this.headerLabel()!) : '');
    protected contentLabelTranslated = computed(() => this.translate.instant(this.contentLabel()));

    protected expectedDocs = computed(() => {
        this.lang(); // Recompute when language changes to update document names in the correct language
        const detail = this.detail();
        return this.orderDocs(this.computeDocs(detail.documentationPackDocs ?? []));
    });

    protected availableDocs = computed(() => this.computeDocs());

    private computeDocs(initialDocs: string[] = []): string[] {
        const detail = this.detail();
        const remove = new Set(this.toRemove());
        const docs = new Set<string>(initialDocs);

        Object.keys(detail.documents).forEach(doc => {
            if (!remove.has(doc)) docs.add(doc);
        });

        Object.keys(detail.data).forEach(doc => {
            if (!remove.has(doc)) docs.add(doc);
        });

        return Array.from(docs);
    }

    private orderDocs(docs: string[]): string[] {
        docs.sort((a, b) => {
            const aTranslated = this.translate.instant(`DOCUMENTS.${a}`);
            const bTranslated = this.translate.instant(`DOCUMENTS.${b}`);
            return aTranslated.localeCompare(bTranslated);
        });
        return docs;
    }

    // #region PDF
    protected showOpenPdfButton(name: string): boolean {
        const url = this.detail().documents[name];
        return url && url.trim() !== '';
    }

    protected openPdf(name: string): void {
	    const url = this.detail().documents[name];
	    if (url) window.open(url, '_blank');
    }
    // #endregion

    // #region JSON
    protected showJsonButtons(name: string): boolean {
        const data = this.detail().data[name];
        return data !== undefined && data !== null && Object.keys(data).length > 0;
    }

    protected openJson(name: string): void {
        localStorage.setItem('jsonViewerObject', JSON.stringify(this.detail().data[name]));

	    // open the JSON viewer in a new tab.
	    const urlTree = this.router.createUrlTree(['json']);
	    const absoluteUrl = `${window.location.origin}${this.router.serializeUrl(urlTree)}`;

	    // open the new tab with the calculated URL
	    window.open(absoluteUrl, '_blank');
    }

    protected downloadJson(name: string): void {
        // Convert the object to a JSON string
        const jsonString = JSON.stringify(this.detail().data[name]);
        // Create a Blob from the JSON string
        const blob = new Blob([jsonString], { type: 'application/json' });
        saveAs(blob, name);
    }
    // #endregion
}
